// ===============================================================
// 💬 ChatRoomPage.js (ChatPage) — Livora Core + Media Support + Block Logic
// - تم التعديل فقط على البطاقات/التأكيدات والتنبيهات داخل التطبيق
// ===============================================================

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import "./ChatRoomPage.css";

import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { getPresenceLabel } from "../utils/presence";
import {
  canSeeReadReceipts,
  isPremiumActive,
  shouldHidePresence,
} from "../utils/vpnPrivacy";

import {
  doc,
  getDoc,
  collection,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import { FaPlus, FaGift, FaPaperPlane, FaVideo } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { uploadChatImage, uploadChatVideo } from "../services/uploadService";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import GiftStorePage from "../gifts/GiftStorePage";

// =========================================================
// ✅ Helpers: PATH / gs:// / downloadURL -> http downloadURL
// =========================================================
function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return "";

  if (!isHttpUrl(value) && !value.startsWith("gs://")) return value;
  if (value.startsWith("gs://")) return value;

  try {
    const part = value.split("/o/")[1];
    if (!part) return "";
    return decodeURIComponent((part.split("?")[0] || "").trim());
  } catch {
    return "";
  }
}

async function resolveToDownloadUrl(value) {
  if (!value) return "";

  if (isHttpUrl(value) && !value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  if (isHttpUrl(value) && value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  try {
    const pathOrGs = storagePathFromValue(value) || value;
    return await getDownloadURL(storageRef(storage, pathOrGs));
  } catch (e) {
    console.warn("resolveToDownloadUrl failed:", value, e);
    return "";
  }
}

// =========================================================
// ✅ Unread helper — supports BOTH schemas
// =========================================================
const MAX_CHAT_IMAGE_BYTES = 10 * 1024 * 1024;

function resetImageFileInput(inputId) {
  const el = document.getElementById(inputId);
  if (el) el.value = "";
}

function resetAllImageFileInputs() {
  resetImageFileInput("inputCamera");
  resetImageFileInput("inputGallery");
}

function getMyUnreadCount(data, myUid) {
  const fromMap =
    data?.unread && typeof data.unread === "object"
      ? data.unread?.[myUid]
      : undefined;

  const fromFlat = data?.[`unread.${myUid}`];

  const n = fromMap != null ? fromMap : fromFlat;
  return Number(n || 0);
}

function ChatPage() {
  const { chatId: paramId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // ✅ FIX: auth user stable (works after refresh)
  const [me, setMe] = useState(null);
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setMe(u));
    return () => unsub();
  }, []);

  const functions = getFunctions(undefined, "us-central1");

  // Existing functions
  const isBlockedFn = httpsCallable(functions, "isBlocked");
  const blockUserFn = httpsCallable(functions, "blockUser");
  const unblockUserFn = httpsCallable(functions, "unblockUser");
  const openChatWithCoinsFn = httpsCallable(functions, "openChatWithCoins");
  const chargeVideoCoinsFn = httpsCallable(functions, "chargeVideoCoins");

  // Chat core functions
  const sendMessageFn = httpsCallable(functions, "sendMessage");
  const markChatDeliveredFn = httpsCallable(functions, "markChatDelivered");
  const markChatReadFn = httpsCallable(functions, "markChatRead");
  const deleteChatForMeFn = httpsCallable(functions, "deleteChatForMe");
  const addGiftFn = httpsCallable(functions, "addGift");

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  // =============================================================
  // حالات الحظر
  // =============================================================
  const [blocked, setBlocked] = useState(false);
  const [iBlockedOther, setIBlockedOther] = useState(false);
  const [otherBlockedMe, setOtherBlockedMe] = useState(false);

  const [showUnblockCard, setShowUnblockCard] = useState(false);
  const [processingUnblock, setProcessingUnblock] = useState(false);

  const [showDeleteCard, setShowDeleteCard] = useState(false);
  const [processingDelete, setProcessingDelete] = useState(false);

  const [showBlockCard, setShowBlockCard] = useState(false);
  const [processingBlock, setProcessingBlock] = useState(false);

  const [showPaidVideoCard, setShowPaidVideoCard] = useState(false);
  const [processingPaidVideo, setProcessingPaidVideo] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    type: "success", // success | error | info
    message: "",
  });

  // =============================================================
  // حالات أساسية
  // =============================================================
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");

  const [chatOpened, setChatOpened] = useState(false);
  /** Firestore chat doc exists and was opened via server; listeners may attach. */
  const [chatReady, setChatReady] = useState(false);
  const [checkingChat, setCheckingChat] = useState(true);

  const [showSendGate, setShowSendGate] = useState(false);
  const [processingSendGate, setProcessingSendGate] = useState(false);

  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [giftStoreOpen, setGiftStoreOpen] = useState(false);
  const [sendingGiftId, setSendingGiftId] = useState("");

  // =============================================================
  // بيانات الطرف الآخر
  // =============================================================
  const [otherUser, setOtherUser] = useState(null);
  const [loadingOtherUser, setLoadingOtherUser] = useState(true);

  const [otherUserId, setOtherUserId] = useState(null);
  const [chatDocId, setChatDocId] = useState(null);

  const messagesEndRef = useRef(null);
  const toastTimerRef = useRef(null);
  const textSendInFlightRef = useRef(false);
  const [textSendBusy, setTextSendBusy] = useState(false);

  // =============================================================
  // ✅ Chat meta + me user doc
  // =============================================================
  const [chatMeta, setChatMeta] = useState(null);
  const [meUserDoc, setMeUserDoc] = useState(null);

  const myGender = String(meUserDoc?.gender || "").trim().toLowerCase();
  const otherGender = String(otherUser?.gender || "").trim().toLowerCase();

  // =============================================================
  // ✅ Media resolved urls (cache)
  // =============================================================
  const [otherAvatarUrl, setOtherAvatarUrl] = useState(() =>
    defaultAvatarByGender("")
  );
  const [mediaMap, setMediaMap] = useState({});

  /** Local preview while image uploads (one at a time). */
  const [uploadingImage, setUploadingImage] = useState(null);
  const uploadingImageUrlRef = useRef(null);

  // =============================================================
  // Toast helper
  // =============================================================
  const showToast = useCallback((type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

    setToast({
      show: true,
      type,
      message,
    });

    toastTimerRef.current = setTimeout(() => {
      setToast({
        show: false,
        type: "success",
        message: "",
      });
    }, 2200);
  }, []);

  const clearUploadingImage = useCallback(() => {
    if (uploadingImageUrlRef.current) {
      URL.revokeObjectURL(uploadingImageUrlRef.current);
      uploadingImageUrlRef.current = null;
    }
    setUploadingImage(null);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (uploadingImageUrlRef.current) {
        URL.revokeObjectURL(uploadingImageUrlRef.current);
        uploadingImageUrlRef.current = null;
      }
    };
  }, []);

  // =============================================================
  // توليد chatId ثابت
  // =============================================================
  const getChatId = useCallback((uid1, uid2) => {
    if (!uid1 || !uid2) return null;
    return uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
  }, []);

  // =============================================================
  // تحديد chatDocId + otherUserId
  // =============================================================
  useEffect(() => {
    const bootstrap = async () => {
      try {
        if (!me || !paramId) return;

        const stateOther = location?.state?.otherUserId || null;

        if (paramId.includes("_")) {
          setChatDocId(paramId);
          const parts = paramId.split("_");
          const other = parts[0] === me.uid ? parts[1] : parts[0];
          setOtherUserId(other || stateOther);
          return;
        }

        const other = stateOther || paramId;
        setOtherUserId(other);
        setChatDocId(getChatId(me.uid, other));
      } catch (e) {
        console.error("bootstrap chat error:", e);
      }
    };

    bootstrap();
  }, [me, paramId, getChatId, location?.state]);

  // =============================================================
  // ✅ Ensure chats/{chatId} exists before client reads (strict rules)
  // =============================================================
  useEffect(() => {
    if (!me?.uid || !chatDocId) {
      setChatReady(false);
      return;
    }

    let cancelled = false;
    setChatReady(false);

    (async () => {
      try {
        const res = await openChatWithCoinsFn({ chatId: chatDocId });
        const data = res?.data || {};
        if (cancelled) return;
        if (data.ok === true) {
          setChatReady(true);
        } else {
          setChatReady(false);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("ensureChat (openChatWithCoins) error:", e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [me?.uid, chatDocId, openChatWithCoinsFn]);

  // =============================================================
  // إغلاق قائمة الثلاث نقاط عند الضغط خارجها
  // =============================================================
  useEffect(() => {
    function handleClickOutside(e) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // =============================================================
  // ✅ Realtime Me UserDoc
  // =============================================================
  useEffect(() => {
    if (!me?.uid) return;

    const unsub = onSnapshot(
      doc(db, "users", me.uid),
      (snap) => setMeUserDoc(snap.exists() ? snap.data() : null),
      (err) => {
        console.error("onSnapshot meUserDoc error:", err);
        setMeUserDoc(null);
      }
    );

    return () => unsub();
  }, [me?.uid]);

  // =============================================================
  // فحص الحظر
  // =============================================================
  useEffect(() => {
    const checkBlockState = async () => {
      try {
        if (!me || !otherUserId) return;

        const res = await isBlockedFn({ otherUid: otherUserId });
        const {
          blocked = false,
          iBlockedOther = false,
          otherBlockedMe = false,
        } = res.data || {};

        setBlocked(blocked === true);
        setIBlockedOther(iBlockedOther === true);
        setOtherBlockedMe(otherBlockedMe === true);
      } catch (err) {
        console.error("check block error:", err);
      }
    };

    checkBlockState();
  }, [me, otherUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // =============================================================
  // فحص حالة فتح المحادثة
  // =============================================================
  const checkChatStatus = useCallback(async () => {
    try {
      if (!me || !chatDocId) return;
      const snap = await getDoc(doc(db, "chats", chatDocId));
      setChatOpened(snap.exists() && snap.data()?.isOpened === true);
    } catch (e) {
      setChatOpened(false);
    } finally {
      setCheckingChat(false);
    }
  }, [me, chatDocId]);

  useEffect(() => {
    if (!me || !chatDocId || !otherUserId) {
      setCheckingChat(false);
      return;
    }
    if (!chatReady) {
      setCheckingChat(false);
      return;
    }
    checkChatStatus();
  }, [me, chatDocId, otherUserId, chatReady, checkChatStatus]);

  // =============================================================
  // ✅ Realtime Chat Meta
  // =============================================================
  useEffect(() => {
    if (!chatDocId) return;

    const unsub = onSnapshot(
      doc(db, "chats", chatDocId),
      (snap) => setChatMeta(snap.exists() ? snap.data() : null),
      (err) => {
        console.error("onSnapshot chatMeta error:", err);
        setChatMeta(null);
      }
    );

    return () => unsub();
  }, [chatDocId]);

  // =============================================================
  // Realtime Messages
  // =============================================================
  useEffect(() => {
    if (!me || !chatDocId) return;

    const q = query(
      collection(db, "chats", chatDocId, "messages"),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];
        snap.forEach((docSnap) => {
          list.push({ id: docSnap.id, ...docSnap.data() });
        });

        setMessages(list);

        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      },
      (err) => {
        console.error("onSnapshot messages error:", err);
      }
    );

    return () => unsub();
  }, [chatDocId, me]);

  // =============================================================
  // ✅ Delivered ALWAYS when chat is open + visible + last msg is for me
  // =============================================================
  useEffect(() => {
    if (!me || !chatDocId || !chatReady) return;
    if (!chatOpened) return;
    if (!chatMeta) return;
    if (document.visibilityState !== "visible") return;

    const myUid = me.uid;
    const lastMsg = messages?.[messages.length - 1];
    if (!lastMsg) return;

    if (lastMsg.fromUid === myUid) return;

    const lastMsgMs =
      lastMsg?.createdAt?.toMillis?.() || Number(lastMsg?.createdAtMs || 0);

    const myDeliveredMs =
      chatMeta?.lastDeliveredAt?.[myUid]?.toMillis?.() ||
      chatMeta?.[`lastDeliveredAt.${myUid}`]?.toMillis?.() ||
      0;

    if (myDeliveredMs && lastMsgMs && myDeliveredMs >= lastMsgMs) return;

    const t1 = setTimeout(() => {
      markChatDeliveredFn({ chatId: chatDocId }).catch(() => {});
    }, 250);

    return () => clearTimeout(t1);
  }, [me, chatDocId, chatReady, chatOpened, chatMeta, messages]);

  // =============================================================
  // ✅ Read only when unread > 0
  // =============================================================
  useEffect(() => {
    if (!me || !chatDocId) return;
    if (!chatMeta) return;
    if (document.visibilityState !== "visible") return;

    const myUid = me.uid;
    const myUnread = getMyUnreadCount(chatMeta, myUid);
    if (myUnread <= 0) return;

    const t2 = setTimeout(() => {
      markChatReadFn({ chatId: chatDocId }).catch((e) =>
        console.error("markChatRead failed:", e)
      );
    }, 400);

    return () => clearTimeout(t2);
  }, [me, chatDocId, chatMeta]);

  // =============================================================
  // Realtime Other User
  // =============================================================
  useEffect(() => {
    if (!otherUserId) return;

    setLoadingOtherUser(true);

    const refUser = doc(db, "publicProfiles", otherUserId);
    const unsub = onSnapshot(
      refUser,
      (snap) => {
        setOtherUser(snap.exists() ? snap.data() : null);
        setLoadingOtherUser(false);
      },
      (err) => {
        console.error("onSnapshot otherUser error:", err);
        setOtherUser(null);
        setLoadingOtherUser(false);
      }
    );

    return () => unsub();
  }, [otherUserId]);

  // =============================================================
  // ✅ Resolve other avatar
  // =============================================================
  useEffect(() => {
    let alive = true;

    const run = async () => {
      const fb = defaultAvatarByGender(otherUser?.gender);

      if (!otherUser) {
        if (alive) setOtherAvatarUrl(fb);
        return;
      }

      const raw =
        otherUser?.profileImage || otherUser?.photoURL || "";

      if (!raw) {
        if (alive) setOtherAvatarUrl(fb);
        return;
      }

      if (isHttpUrl(raw) && !raw.includes("firebasestorage.googleapis.com")) {
        if (alive) setOtherAvatarUrl(raw);
        return;
      }

      const url = await resolveToDownloadUrl(raw);
      if (alive) setOtherAvatarUrl(url || fb);
    };

    run();
    return () => {
      alive = false;
    };
  }, [otherUser]);

  // =============================================================
  // ✅ Resolve media urls in messages
  // =============================================================
  useEffect(() => {
    let alive = true;

    const run = async () => {
      const updates = {};
      const tasks = [];

      for (const m of messages) {
        if (m.type === "image" && m.imageUrl && !mediaMap[m.id]?.imageUrl) {
          tasks.push(
            resolveToDownloadUrl(m.imageUrl).then((url) => {
              if (url) {
                updates[m.id] = {
                  ...(updates[m.id] || {}),
                  imageUrl: url,
                };
              }
            })
          );
        }

        if (m.type === "video" && m.videoUrl && !mediaMap[m.id]?.videoUrl) {
          tasks.push(
            resolveToDownloadUrl(m.videoUrl).then((url) => {
              if (url) {
                updates[m.id] = {
                  ...(updates[m.id] || {}),
                  videoUrl: url,
                };
              }
            })
          );
        }
      }

      if (!tasks.length) return;

      await Promise.all(tasks);

      if (alive && Object.keys(updates).length) {
        setMediaMap((prev) => {
          const next = { ...prev };
          for (const k of Object.keys(updates)) {
            next[k] = { ...(next[k] || {}), ...updates[k] };
          }
          return next;
        });
      }
    };

    run();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // =============================================================
  // فتح البروفايل
  // =============================================================
  const handleOpenProfile = () => {
    if (!otherUserId) return;

    if (otherUserId === me?.uid) navigate("/my-profile");
    else navigate(`/user/${otherUserId}`);
  };

  // =============================================================
  // 🚫 فتح بطاقة رفع الحظر
  // =============================================================
  const openUnblockCard = () => {
    setShowPlusMenu(false);
    setShowMoreMenu(false);
    setShowSendGate(false);
    setShowPaidVideoCard(false);
    setShowUnblockCard(true);
  };

  // =============================================================
  // 🔓 فك الحظر
  // =============================================================
  const handleUnblockUser = async ({ continueAfter = false } = {}) => {
    if (!me || !otherUserId) return;

    try {
      setProcessingUnblock(true);

      await unblockUserFn({
        blockedUid: otherUserId,
      });

      setBlocked(false);
      setIBlockedOther(false);
      setOtherBlockedMe(false);
      setShowUnblockCard(false);
      setShowMoreMenu(false);

      if (continueAfter) {
        if (!chatOpened) {
          if (myGender === "female" && chatDocId) {
            try {
              const textToSend = (inputText || "").trim();
              if (textToSend) {
                if (!chatOpened || !chatReady) {
                  await openChatWithCoinsFn({ chatId: chatDocId, cost: 5 });
                  setChatOpened(true);
                  setChatReady(true);
                }
                await sendMessageFn({
                  chatId: chatDocId,
                  kind: "text",
                  type: "text",
                  text: textToSend,
                });
                setInputText("");
              } else {
                await openChatWithCoinsFn({ chatId: chatDocId, cost: 5 });
                setChatOpened(true);
                setChatReady(true);
              }
            } catch (e) {
              console.error("unblock open chat error:", e);
              showToast(
                "error",
                t("chat.open_failed", {
                  defaultValue: "تعذر فتح المحادثة. تأكد من الرصيد وحاول مرة أخرى",
                })
              );
              return;
            }
          } else if (myGender !== "female") {
            setShowSendGate(true);
            return;
          } else {
            return;
          }
        } else {
          const textToSend = (inputText || "").trim();
          if (textToSend) {
            await sendMessageFn({
              chatId: chatDocId,
              kind: "text",
              text: textToSend,
            });
            setInputText("");
          }
        }
      }

      showToast(
        "success",
        t("chat.unblock_success", {
          defaultValue: "تم رفع الحظر بنجاح",
        })
      );
    } catch (e) {
      console.error("unblockUser error:", e);
      showToast(
        "error",
        t("general_error", {
          defaultValue: "حدث خطأ",
        })
      );
    } finally {
      setProcessingUnblock(false);
    }
  };

  // =============================================================
  // إرسال نص
  // =============================================================
  const handleSendAttempt = async () => {
    if (!me) return;
    if (textSendInFlightRef.current) return;

    if (iBlockedOther) {
      openUnblockCard();
      return;
    }

    if (otherBlockedMe) {
      showToast(
        "error",
        t("chat.blocked_by_other", {
          defaultValue: "لا يمكنك إرسال الرسائل لهذا المستخدم",
        })
      );
      return;
    }

    if (myGender === "female" && (!chatOpened || !chatReady)) {
      if (!inputText || !inputText.trim()) return;
      if (!chatDocId) {
        showToast(
          "error",
          t("chat.id_not_ready", {
            defaultValue: "معرّف المحادثة غير جاهز بعد، حاول مرة ثانية",
          })
        );
        return;
      }
      if (textSendInFlightRef.current) return;
      textSendInFlightRef.current = true;
      setTextSendBusy(true);
      const textToSend = inputText.trim();
      try {
        if (!chatOpened || !chatReady) {
          await openChatWithCoinsFn({ chatId: chatDocId });
          setChatOpened(true);
          setChatReady(true);
        }
        await sendMessageFn({
          chatId: chatDocId,
          kind: "text",
          type: "text",
          text: textToSend,
        });
        setInputText("");
      } catch (e) {
        console.error("female send error:", e);
        showToast(
          "error",
          t("chat.send_failed", {
            defaultValue: "تعذر إرسال الرسالة (صلاحيات/اتصال).",
          })
        );
      } finally {
        textSendInFlightRef.current = false;
        setTextSendBusy(false);
      }
      return;
    }

    if (!chatOpened) {
      setShowSendGate(true);
      return;
    }

    if (!inputText || !inputText.trim()) return;

    if (textSendInFlightRef.current) return;
    textSendInFlightRef.current = true;
    setTextSendBusy(true);
    const textToSend = inputText.trim();
    try {
      await sendMessageFn({
        chatId: chatDocId,
        kind: "text",
        text: textToSend,
      });

      setInputText("");
    } catch (e) {
      console.error("SEND MESSAGE ERROR:", e);
      showToast(
        "error",
        t("chat.send_failed", {
          defaultValue: "تعذر إرسال الرسالة (صلاحيات/اتصال).",
        })
      );
    } finally {
      textSendInFlightRef.current = false;
      setTextSendBusy(false);
    }
  };

  // =============================================================
  // الهدايا (ذكر → أنثى في المحادثة فقط)
  // =============================================================
  const canSendChatGifts =
    myGender === "male" && otherGender === "female";

  const handleGiftClick = () => {
    if (!canSendChatGifts) return;
    setGiftStoreOpen(true);
  };

  const handleChatSendGift = useCallback(
    async (gift) => {
      if (!canSendChatGifts || !otherUserId || !chatDocId || !gift) return;

      try {
        setSendingGiftId(String(gift.id || ""));
        const res = await addGiftFn({
          toUid: otherUserId,
          chatId: chatDocId,
          giftId: String(gift.id || ""),
          contextType: "chat",
        });
        if (res?.data?.ok) {
          setGiftStoreOpen(false);
        } else {
          alert(
            t("chat.gift_failed", {
              defaultValue: "تعذر إرسال الهدية",
            })
          );
        }
      } catch (e) {
        console.error("addGift chat error:", e);
        const msg =
          e?.message ||
          t("chat.gift_failed", { defaultValue: "تعذر إرسال الهدية" });
        alert(msg);
      } finally {
        setSendingGiftId("");
      }
    },
    [canSendChatGifts, otherUserId, chatDocId, addGiftFn, t]
  );

  // =============================================================
  // 🚫 فتح بطاقة حظر المستخدم
  // =============================================================
  const openBlockCard = () => {
    setShowMoreMenu(false);
    setShowDeleteCard(false);
    setShowSendGate(false);
    setShowUnblockCard(false);
    setShowPaidVideoCard(false);
    setShowBlockCard(true);
  };

  // =============================================================
  // 🚫 حظر المستخدم
  // =============================================================
  const handleBlockUser = async () => {
    if (!me || !otherUserId) return;

    try {
      setProcessingBlock(true);

      await blockUserFn({
        blockedUid: otherUserId,
        reason: "chat_manual_block",
      });

      setBlocked(true);
      setIBlockedOther(true);
      setOtherBlockedMe(false);
      setShowMoreMenu(false);
      setShowBlockCard(false);

      showToast(
        "success",
        t("chat.block_success", {
          defaultValue: "تم حظر المستخدم بنجاح",
        })
      );
    } catch (e) {
      console.error("blockUser error:", e);
      showToast(
        "error",
        t("general_error", {
          defaultValue: "حدث خطأ أثناء الحظر",
        })
      );
    } finally {
      setProcessingBlock(false);
    }
  };

  // =============================================================
  // زر الفيديو العلوي
  // =============================================================
  const handleVideoClick = () => {
    console.log("TOP VIDEO BUTTON");
  };

  // =============================================================
  // زر +
  // =============================================================
  const handlePlusClick = () => {
    setShowSendGate(false);
    setShowMoreMenu(false);
    setShowPaidVideoCard(false);
    setShowPlusMenu((v) => !v);
  };

  // =============================================================
  // كاميرا / معرض
  // =============================================================
  const handleCameraImage = () => {
    if (uploadingImage) return;

    if (iBlockedOther) return openUnblockCard();

    if (otherBlockedMe) {
      showToast(
        "error",
        t("chat.blocked_by_other", {
          defaultValue: "لا يمكنك إرسال الوسائط لهذا المستخدم",
        })
      );
      return;
    }

    if (!chatDocId) return setShowSendGate(true);

    document.getElementById("inputCamera")?.click();
    setShowPlusMenu(false);
  };

  const handleGalleryImage = () => {
    if (uploadingImage) return;

    if (iBlockedOther) return openUnblockCard();

    if (otherBlockedMe) {
      showToast(
        "error",
        t("chat.blocked_by_other", {
          defaultValue: "لا يمكنك إرسال الوسائط لهذا المستخدم",
        })
      );
      return;
    }

    if (!chatDocId) return setShowSendGate(true);

    document.getElementById("inputGallery")?.click();
    setShowPlusMenu(false);
  };

  // =============================================================
  // فيديو 10 كوينز — فتح بطاقة التأكيد
  // =============================================================
  const handlePaidVideo = async () => {
    if (!me) {
      showToast(
        "error",
        t("auth.required", { defaultValue: "يجب تسجيل الدخول أولاً" })
      );
      return;
    }

    if (iBlockedOther) return openUnblockCard();

    if (otherBlockedMe) {
      showToast(
        "error",
        t("chat.blocked_by_other", {
          defaultValue: "لا يمكنك إرسال الوسائط لهذا المستخدم",
        })
      );
      return;
    }

    if (!chatDocId) return setShowSendGate(true);

    if (!chatDocId || !otherUserId) {
      showToast(
        "error",
        t("chat.not_ready", {
          defaultValue: "المحادثة غير جاهزة بعد، حاول مرة ثانية",
        })
      );
      return;
    }

    setShowPlusMenu(false);
    setShowPaidVideoCard(true);
  };

  // =============================================================
  // تأكيد الفيديو المدفوع
  // =============================================================
  const handleConfirmPaidVideo = async () => {
    try {
      if (!chatDocId || !otherUserId) return;

      setProcessingPaidVideo(true);

      await chargeVideoCoinsFn({
        chatId: chatDocId,
        toUid: otherUserId,
        cost: 10,
      });

      setShowPaidVideoCard(false);
      document.getElementById("inputVideo")?.click();
    } catch (e) {
      console.error("chargeVideoCoins error:", e);
      showToast(
        "error",
        t("chat.video_failed", {
          defaultValue: "تعذر إرسال الفيديو. تأكد من الرصيد وحاول مرة أخرى",
        })
      );
    } finally {
      setProcessingPaidVideo(false);
    }
  };

  // =============================================================
  // بعد اختيار صورة
  // =============================================================
  const handleImageSelected = async (e) => {
    const inputEl = e.target;

    try {
      if (!me) return;

      if (uploadingImage) {
        resetAllImageFileInputs();
        return;
      }

      if (iBlockedOther) {
        openUnblockCard();
        resetAllImageFileInputs();
        return;
      }

      if (otherBlockedMe) {
        showToast(
          "error",
          t("chat.blocked_by_other", {
            defaultValue: "لا يمكنك إرسال الوسائط لهذا المستخدم",
          })
        );
        resetAllImageFileInputs();
        return;
      }

      if (!chatDocId) {
        setShowSendGate(true);
        resetAllImageFileInputs();
        return;
      }

      const file = inputEl.files?.[0];
      if (!file) return;

      if (!file.type || !file.type.startsWith("image/")) {
        showToast(
          "error",
          t("chat.image_invalid_type", {
            defaultValue: "نوع الملف غير مدعوم. اختر صورة فقط.",
          })
        );
        resetAllImageFileInputs();
        return;
      }

      if (file.size > MAX_CHAT_IMAGE_BYTES) {
        showToast(
          "error",
          t("chat.image_too_large", {
            defaultValue: "حجم الصورة كبير جداً. الحد الأقصى 10 ميجابايت.",
          })
        );
        resetAllImageFileInputs();
        return;
      }

      setShowPlusMenu(false);
      document.querySelector(".message-input")?.focus();

      const objectUrl = URL.createObjectURL(file);
      uploadingImageUrlRef.current = objectUrl;
      setUploadingImage({ objectUrl });

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);

      const url = await uploadChatImage(chatDocId, file);

      await sendMessageFn({
        chatId: chatDocId,
        kind: "image",
        mediaUrl: url,
      });
    } catch (err) {
      console.error("image send error", err);
      showToast(
        "error",
        t("chat.image_failed", { defaultValue: "تعذر إرسال الصورة" })
      );
    } finally {
      clearUploadingImage();
      resetAllImageFileInputs();
    }
  };

  // =============================================================
  // بعد اختيار فيديو
  // =============================================================
  const handleVideoSelected = async (e) => {
    try {
      if (!me) return;

      if (iBlockedOther) {
        openUnblockCard();
        return;
      }

      if (otherBlockedMe) {
        showToast(
          "error",
          t("chat.blocked_by_other", {
            defaultValue: "لا يمكنك إرسال الوسائط لهذا المستخدم",
          })
        );
        return;
      }

      if (!chatDocId) return setShowSendGate(true);

      const file = e.target.files?.[0];
      if (!file) return;

      setShowPlusMenu(false);
      document.querySelector(".message-input")?.focus();

      const url = await uploadChatVideo(chatDocId, file);

      await sendMessageFn({
        chatId: chatDocId,
        kind: "video",
        mediaUrl: url,
      });
    } catch (err) {
      console.error("video send error", err);
      showToast(
        "error",
        t("chat.video_failed_upload", { defaultValue: "تعذر إرسال الفيديو" })
      );
    }
  };

  // =============================================================
  // تأكيد فتح الشات لأول مرة
  // =============================================================
  const handleConfirmGate = async () => {
    try {
      if (!me) {
        showToast(
          "error",
          t("auth.required", { defaultValue: "يجب تسجيل الدخول أولاً" })
        );
        return;
      }

      if (iBlockedOther) {
        openUnblockCard();
        return;
      }

      if (otherBlockedMe) {
        showToast(
          "error",
          t("chat.blocked_by_other", {
            defaultValue: "لا يمكنك فتح المحادثة مع هذا المستخدم",
          })
        );
        return;
      }

      if (!chatDocId) {
        showToast(
          "error",
          t("chat.id_not_ready", {
            defaultValue: "معرّف المحادثة غير جاهز بعد، حاول مرة ثانية",
          })
        );
        return;
      }

      setProcessingSendGate(true);

      await openChatWithCoinsFn({
        chatId: chatDocId,
        cost: 5,
      });

      setChatOpened(true);
      setChatReady(true);
      setShowSendGate(false);
    } catch (e) {
      console.error("handleConfirmGate error:", e);
      showToast(
        "error",
        t("chat.open_failed", {
          defaultValue: "تعذر فتح المحادثة. تأكد من الرصيد وحاول مرة أخرى",
        })
      );
    } finally {
      setProcessingSendGate(false);
    }
  };

  // =============================================================
  // حذف المحادثة
  // =============================================================
  const handleDeleteChat = async () => {
    try {
      if (!chatDocId) return;

      setProcessingDelete(true);

      await deleteChatForMeFn({ chatId: chatDocId });

      setShowDeleteCard(false);
      navigate("/messages");
    } catch (e) {
      console.error("deleteChatForMe error:", e);
      showToast(
        "error",
        t("chat.delete_failed", {
          defaultValue: "تعذر حذف المحادثة",
        })
      );
    } finally {
      setProcessingDelete(false);
    }
  };

  // =============================================================
  // ticks helpers
  // =============================================================
  function getTsMs(meta, base, uid) {
    if (!meta || !uid) return 0;

    const fromMap =
      meta?.[base] && typeof meta[base] === "object"
        ? meta[base]?.[uid]
        : null;

    const fromFlat = meta?.[`${base}.${uid}`];

    const v = fromMap || fromFlat;
    return v?.toMillis?.() ? v.toMillis() : 0;
  }

  const getTicks = (msg) => {
    const isMine = msg?.fromUid === me?.uid;
    if (!isMine) return null;

    const msgMs =
      Number(msg?.createdAtMs || 0) || msg?.createdAt?.toMillis?.() || 0;

    if (!canSeeReadReceipts(meUserDoc)) {
      return <span className="msg-ticks one">✓</span>;
    }

    const otherDeliveredMs = getTsMs(chatMeta, "lastDeliveredAt", otherUserId);
    const otherReadMs = getTsMs(chatMeta, "lastReadAt", otherUserId);

    const isDelivered =
      otherDeliveredMs && msgMs && otherDeliveredMs >= msgMs;
    const isRead = otherReadMs && msgMs && otherReadMs >= msgMs;

    if (!isDelivered) return <span className="msg-ticks one">✓</span>;

    return (
      <span className={`msg-ticks two ${isRead ? "read" : "delivered"}`}>
        ✓✓
      </span>
    );
  };

  const viewerCanSee = isPremiumActive(meUserDoc);

  const otherHasPrivacy =
    isPremiumActive(otherUser) && shouldHidePresence(otherUser);

  const otherPresenceLabel =
    !loadingOtherUser &&
    otherUser &&
    viewerCanSee &&
    !otherHasPrivacy
      ? getPresenceLabel(otherUser, t)
      : "";

  // =============================================================
  // RENDER
  // =============================================================
  return (
    <div className="chat-page">
      {/* ===================== TopBar ===================== */}
      <div className="chat-topbar">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <span className="back-arrow"> ‹ </span>
        </button>

        <div className="chat-topbar-user" onClick={handleOpenProfile}>
          <img
            className="chat-topbar-avatar"
            src={otherAvatarUrl}
            alt={t("common.avatar", { defaultValue: "avatar" })}
            onError={(e) =>
              (e.currentTarget.src = defaultAvatarByGender(
                otherUser?.gender
              ))
            }
          />

          <div className="chat-topbar-info">
            <div className="chat-topbar-name">
              {loadingOtherUser
                ? "..."
                : otherUser?.name ||
                  otherUser?.displayName ||
                  t("common.user", { defaultValue: "User" })}
            </div>

            {otherPresenceLabel ? (
              <span className="chat-status">{otherPresenceLabel}</span>
            ) : null}
          </div>
        </div>

        <button className="chat-topbar-video" onClick={handleVideoClick}>
          <FaVideo />
        </button>

        <button
          className="chat-topbar-more"
          onClick={() => setShowMoreMenu((v) => !v)}
        >
          ⋮
        </button>

        {showMoreMenu && (
          <div className="more-menu" ref={moreMenuRef}>
            <button
              onClick={() => {
                setShowMoreMenu(false);
                setShowDeleteCard(true);
              }}
            >
              🗑 {t("delete_chat", { defaultValue: "حذف المحادثة" })}
            </button>

            {iBlockedOther ? (
              <button
                onClick={() => {
                  setShowMoreMenu(false);
                  setShowUnblockCard(true);
                }}
              >
                🔓 {t("unblock", { defaultValue: "إلغاء الحظر" })}
              </button>
            ) : (
              <button onClick={openBlockCard}>
                🚫 {t("block_user", { defaultValue: "حظر المستخدم" })}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ===================== Toast ===================== */}
      {toast.show && (
        <div className={`livora-toast ${toast.type}`}>{toast.message}</div>
      )}

      {/* ===================== Card رفع الحظر ===================== */}
      {showUnblockCard && iBlockedOther && (
        <div className="gate-overlay" onClick={() => setShowUnblockCard(false)}>
          <div
            className="gatecard-center"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-live="polite"
          >
            <div className="gatecard-title">
              {t("chat.unblock_title", {
                defaultValue: "هذا المستخدم محظور",
              })}
            </div>

            <div className="gatecard-sub">
              {t("chat.unblock_sub", {
                defaultValue:
                  "لقد قمت بحظر هذا المستخدم. هل تريد رفع الحظر للمتابعة؟",
              })}
            </div>

            <div className="gatecard-actions">
              <button
                type="button"
                className="gate-btn gate-btn-cancel"
                onClick={() => setShowUnblockCard(false)}
                disabled={processingUnblock}
              >
                {t("cancel", { defaultValue: "إلغاء" })}
              </button>

              <button
                type="button"
                className="gate-btn gate-btn-pay"
                onClick={() => handleUnblockUser({ continueAfter: true })}
                disabled={processingUnblock}
              >
                {processingUnblock
                  ? t("processing", { defaultValue: "جارٍ المعالجة..." })
                  : t("unblock", { defaultValue: "إلغاء الحظر" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Card حذف المحادثة ===================== */}
      {showDeleteCard && (
        <div className="gate-overlay" onClick={() => setShowDeleteCard(false)}>
          <div
            className="gatecard-center"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-live="polite"
          >
            <div className="gatecard-title">
              {t("chat.delete_title", {
                defaultValue: "حذف المحادثة",
              })}
            </div>

            <div className="gatecard-sub">
              {t("chat.delete_confirm", {
                defaultValue:
                  "سيتم حذف هذه المحادثة من جهازك فقط، ولن تُحذف من جهاز الطرف الآخر.",
              })}
            </div>

            <div className="gatecard-actions">
              <button
                type="button"
                className="gate-btn gate-btn-cancel"
                onClick={() => setShowDeleteCard(false)}
                disabled={processingDelete}
              >
                {t("cancel", { defaultValue: "إلغاء" })}
              </button>

              <button
                type="button"
                className="gate-btn gate-btn-danger"
                onClick={handleDeleteChat}
                disabled={processingDelete}
              >
                {processingDelete
                  ? t("processing", { defaultValue: "جارٍ الحذف..." })
                  : t("delete_chat", { defaultValue: "حذف المحادثة" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Card حظر المستخدم ===================== */}
      {showBlockCard && (
        <div className="gate-overlay" onClick={() => setShowBlockCard(false)}>
          <div
            className="gatecard-center"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-live="polite"
          >
            <div className="gatecard-title">
              {t("chat.block_title", {
                defaultValue: "حظر المستخدم",
              })}
            </div>

            <div className="gatecard-sub">
              {t("chat.block_confirm", {
                defaultValue:
                  "هل أنت متأكد أنك تريد حظر هذا المستخدم؟ لن تتمكن من إرسال أو استقبال الرسائل منه حتى تقوم بإلغاء الحظر.",
              })}
            </div>

            <div className="gatecard-actions">
              <button
                type="button"
                className="gate-btn gate-btn-cancel"
                onClick={() => setShowBlockCard(false)}
                disabled={processingBlock}
              >
                {t("cancel", { defaultValue: "إلغاء" })}
              </button>

              <button
                type="button"
                className="gate-btn gate-btn-danger"
                onClick={handleBlockUser}
                disabled={processingBlock}
              >
                {processingBlock
                  ? t("processing", { defaultValue: "جارٍ المعالجة..." })
                  : t("block_user", { defaultValue: "حظر المستخدم" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Gate Card ===================== */}
      {showSendGate && !chatOpened && myGender === "male" && (
        <div className="gate-overlay" onClick={() => setShowSendGate(false)}>
          <div
            className="gatecard-center"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-live="polite"
          >
            <div className="gatecard-title">
              {t("chat.gate_title", { defaultValue: "للمتابعة" })}
            </div>

            <div className="gatecard-sub">
              {t("chat.gate_sub", {
                defaultValue:
                  "يجب الدفع {{cost}} كوينز لمرة واحدة لفتح المحادثة نهائيًا.",
                cost: 5,
              })}
            </div>

            <div className="gatecard-actions">
              <button
                type="button"
                className="gate-btn gate-btn-cancel"
                onClick={() => setShowSendGate(false)}
                disabled={processingSendGate}
              >
                {t("chat.gate_cancel", { defaultValue: "إلغاء" })}
              </button>

              <button
                type="button"
                className="gate-btn gate-btn-pay"
                onClick={handleConfirmGate}
                disabled={processingSendGate}
              >
                {processingSendGate ? (
                  t("processing", { defaultValue: "جارٍ المعالجة..." })
                ) : (
                  <>
                    <span>🪙</span>
                    <span>5</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Card فيديو مدفوع ===================== */}
      {showPaidVideoCard && (
        <div
          className="gate-overlay"
          onClick={() => !processingPaidVideo && setShowPaidVideoCard(false)}
        >
          <div
            className="gatecard-center"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-live="polite"
          >
            <div className="gatecard-title">
              {t("chat.video_paid_title", {
                defaultValue: "إرسال فيديو",
              })}
            </div>

            <div className="gatecard-sub">
              {t("chat.video_cost_confirm", {
                defaultValue: "إرسال فيديو يتطلب 10 كوينز — هل تريد المتابعة؟",
              })}
            </div>

            <div className="gatecard-actions">
              <button
                type="button"
                className="gate-btn gate-btn-cancel"
                onClick={() => setShowPaidVideoCard(false)}
                disabled={processingPaidVideo}
              >
                {t("cancel", { defaultValue: "إلغاء" })}
              </button>

              <button
                type="button"
                className="gate-btn gate-btn-pay"
                onClick={handleConfirmPaidVideo}
                disabled={processingPaidVideo}
              >
                {processingPaidVideo ? (
                  t("processing", { defaultValue: "جارٍ المعالجة..." })
                ) : (
                  <>
                    <span>🪙</span>
                    <span>10</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Inputs Hidden ===================== */}
      <input
        type="file"
        id="inputCamera"
        accept="image/*"
        capture="environment"
        hidden
        onChange={handleImageSelected}
      />
      <input
        type="file"
        id="inputGallery"
        accept="image/*"
        hidden
        onChange={handleImageSelected}
      />
      <input
        type="file"
        id="inputVideo"
        accept="video/*"
        hidden
        onChange={handleVideoSelected}
      />

      {/* ===================== Messages List ===================== */}
      <div className="chat-messages">
        {messages.map((m) => {
          const mine = m.fromUid === me?.uid;
          const resolvedImg = mediaMap[m.id]?.imageUrl || "";
          const resolvedVid = mediaMap[m.id]?.videoUrl || "";

          return (
            <div key={m.id} className={`msg-row ${mine ? "mine" : "theirs"}`}>
              {m.type === "text" && (
                <div className="msg-bubble" dir="auto">
                  {m.text}
                  {getTicks(m)}
                </div>
              )}

              {m.type === "image" && (
                <a
                  className="msg-image-card"
                  href={resolvedImg || "#"}
                  target="_blank"
                  rel="noreferrer"
                  dir="auto"
                  onClick={(e) => {
                    if (!resolvedImg) e.preventDefault();
                  }}
                >
                  <img
                    className="msg-image"
                    src={
                      resolvedImg ||
                      defaultAvatarByGender(
                        mine ? meUserDoc?.gender : otherUser?.gender
                      )
                    }
                    alt={t("common.image", { defaultValue: "img" })}
                  />
                </a>
              )}

              {m.type === "video" && (
                <video className="msg-video" src={resolvedVid || ""} controls />
              )}

              {m.type === "gift" && (
                <div className="msg-bubble msg-bubble--gift" dir="auto">
                  <span className="msg-gift-emoji" aria-hidden>
                    🎁
                  </span>{" "}
                  <span className="msg-gift-name">
                    {m.giftName || m.giftId || t("chat.gift", { defaultValue: "Gift" })}
                  </span>
                  {m.giftValue != null && Number.isFinite(Number(m.giftValue)) ? (
                    <span className="msg-gift-meta">
                      {" "}
                      ·{" "}
                      {t("wallet.coins", {
                        defaultValue: "{{count}} Coins",
                        count: Number(m.giftValue),
                      })}
                    </span>
                  ) : null}
                  {getTicks(m)}
                </div>
              )}
            </div>
          );
        })}

        {uploadingImage?.objectUrl ? (
          <div className="msg-row mine">
            <div
              className="msg-image-card msg-image-card--uploading"
              aria-busy="true"
              aria-live="polite"
            >
              <img
                className="msg-image"
                src={uploadingImage.objectUrl}
                alt=""
              />
              <div className="msg-image-card__overlay">
                <span className="msg-image-card__spinner" aria-hidden />
                <span className="msg-image-card__status">
                  {t("chat.image_sending", {
                    defaultValue: "جاري إرسال الصورة...",
                  })}
                </span>
              </div>
            </div>
          </div>
        ) : null}

        <div ref={messagesEndRef} />
      </div>

      {/* ===================== Input Bar ===================== */}
      <div className="chat-input-bar">
        <button
          type="button"
          className="input-icon-btn plus-btn"
          onClick={handlePlusClick}
          disabled={!!uploadingImage}
          aria-disabled={!!uploadingImage}
        >
          <FaPlus />
        </button>

        {showPlusMenu && (
          <div
            className="plus-menu-overlay"
            onClick={() => setShowPlusMenu(false)}
          >
            <div className="plus-menu" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={handleCameraImage}
                disabled={!!uploadingImage}
              >
                📷 {t("chat.camera", { defaultValue: "الكاميرا" })}
              </button>
              <button
                type="button"
                onClick={handleGalleryImage}
                disabled={!!uploadingImage}
              >
                🖼️ {t("chat.gallery", { defaultValue: "المعرض" })}
              </button>
              <button onClick={handlePaidVideo}>
                🎥 {t("chat.video_paid", { defaultValue: "فيديو (مدفوع)" })}
              </button>
            </div>
          </div>
        )}

        <textarea
          className="message-input"
          dir="auto"
          rows={1}
          value={inputText}
          disabled={otherBlockedMe}
          placeholder={
            otherBlockedMe
              ? t("chat.blocked_by_other_placeholder", {
                  defaultValue: "لا يمكنك مراسلة هذا المستخدم",
                })
              : iBlockedOther
              ? t("chat.i_blocked_other_placeholder", {
                  defaultValue:
                    "لقد قمت بحظر هذا المستخدم. اضغط إرسال لرفع الحظر والمتابعة",
                })
              : chatOpened
              ? t("write_message", { defaultValue: "اكتب رسالة..." })
              : myGender === "female"
              ? t("write_message", { defaultValue: "اكتب رسالة..." })
              : t("first_message_cost", {
                  defaultValue: "Sending the first message costs 5 coins",
                })
          }
          onChange={(e) => {
            setInputText(e.target.value);
            setShowPlusMenu(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendAttempt();
            }
          }}
        />

        {(canSendChatGifts || myGender === "female") && (
          <button
            type="button"
            className="input-icon-btn gift-btn"
            onClick={handleGiftClick}
            disabled={!canSendChatGifts}
            title={
              !canSendChatGifts && myGender === "female"
                ? t("chat.gifts_males_only", {
                    defaultValue: "الهدايا متاحة للشباب فقط",
                  })
                : undefined
            }
          >
            <FaGift />
          </button>
        )}

        <button
          className="send-btn"
          type="button"
          onClick={handleSendAttempt}
          disabled={textSendBusy}
        >
          <FaPaperPlane />
        </button>
      </div>

      {giftStoreOpen && (
        <div className="chat-giftstore-root">
          <button
            type="button"
            className="chat-giftstore-backdrop"
            aria-label={t("close", { defaultValue: "Close" })}
            onClick={() => setGiftStoreOpen(false)}
          />
          <div className="chat-giftstore-layer">
            <GiftStorePage
              open={giftStoreOpen}
              onClose={() => setGiftStoreOpen(false)}
              onSendGift={handleChatSendGift}
              sendingGiftId={sendingGiftId}
              viewerRole={myGender}
              defaultTab="small"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatPage;